
import { useRef } from 'react';
import { SpeechRecognition } from '@/types/speechRecognition';

interface UseSpeechRecognitionProps {
  onSpeechEnd: () => void;
  debug?: boolean;
}

export const useSpeechRecognition = ({ onSpeechEnd, debug = false }: UseSpeechRecognitionProps) => {
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isRunningRef = useRef(false);
  const hasDetectedSpeechRef = useRef(false);
  const speechEndTimeoutRef = useRef<number | null>(null);

  const initSpeechRecognition = () => {
    // Prioritize webkit Speech Recognition API
    const SpeechRecognition = (window as any).webkitSpeechRecognition || 
                             (window as any).SpeechRecognition;
    
    if (!SpeechRecognition) {
      if (debug) console.warn("🗣️ Speech recognition not supported in this browser");
      return null;
    }
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true; // Keep listening continuously
    recognition.interimResults = true; // Get interim results
    recognition.lang = 'en-US';
    
    // When speech starts
    recognition.onspeechstart = () => {
      hasDetectedSpeechRef.current = true;
      if (debug) console.log("🗣️ Speech started (webkit)");
      
      // Clear any pending timeout when speech starts
      if (speechEndTimeoutRef.current) {
        clearTimeout(speechEndTimeoutRef.current);
        speechEndTimeoutRef.current = null;
      }
    };
    
    // When speech ends - add a delay before actually stopping
    recognition.onspeechend = () => {
      if (debug) console.log("🛑 WebKit SpeechRecognition: Speech ended, waiting for silence...");
      
      // Don't immediately stop - wait for a longer pause to confirm speech is really done
      // Let silence detection handle the actual stopping
    };
    
    // When recognition ends
    recognition.onend = () => {
      if (debug) console.log("🏁 WebKit SpeechRecognition: Recognition ended");
      isRunningRef.current = false;
      
      // Restart recognition if we're still supposed to be listening
      // This handles the case where recognition stops unexpectedly
      if (hasDetectedSpeechRef.current) {
        setTimeout(() => {
          if (!isRunningRef.current) {
            startSpeechRecognition();
          }
        }, 100);
      }
    };
    
    // Handle errors - be more tolerant of speech recognition errors
    recognition.onerror = (event) => {
      if (debug) console.error("❌ WebKit SpeechRecognition error:", event.error);
      
      // Only stop on serious errors, not on expected ones
      const seriousErrors = ['not-allowed', 'service-not-allowed', 'bad-grammar'];
      if (seriousErrors.includes(event.error)) {
        isRunningRef.current = false;
        if (hasDetectedSpeechRef.current) {
          onSpeechEnd();
        }
      }
      // For other errors like 'no-speech', 'aborted', just continue
    };
    
    // Handle results - this gives us more control over when to stop
    recognition.onresult = (event) => {
      // We have results, so speech was definitely detected
      hasDetectedSpeechRef.current = true;
      
      // Check if we have a final result
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          if (debug) console.log("🎯 Final speech result received (webkit)");
          // Let silence detection handle the stopping after final result
        }
      }
    };
    
    return recognition;
  };

  const startSpeechRecognition = () => {
    if (!recognitionRef.current) {
      recognitionRef.current = initSpeechRecognition();
    }
    
    if (recognitionRef.current && !isRunningRef.current) {
      try {
        hasDetectedSpeechRef.current = false; // Reset speech detection flag
        
        // Clear any existing timeout
        if (speechEndTimeoutRef.current) {
          clearTimeout(speechEndTimeoutRef.current);
          speechEndTimeoutRef.current = null;
        }
        
        recognitionRef.current.start();
        isRunningRef.current = true;
        if (debug) console.log("🎤 WebKit Speech recognition started");
      } catch (error) {
        if (debug) console.warn("Failed to start webkit speech recognition:", error);
        isRunningRef.current = false;
      }
    }
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current && isRunningRef.current) {
      try {
        recognitionRef.current.stop();
        recognitionRef.current.abort(); // Force stop
        isRunningRef.current = false;
        if (debug) console.log("🛑 WebKit Speech recognition stopped and aborted");
      } catch (error) {
        if (debug) console.warn("Error stopping webkit speech recognition:", error);
        isRunningRef.current = false;
      }
    }
    
    // Clear any pending timeout
    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
      speechEndTimeoutRef.current = null;
    }
  };

  const cleanupSpeechRecognition = () => {
    stopSpeechRecognition();
    recognitionRef.current = null;
    hasDetectedSpeechRef.current = false;
    if (debug) console.log("🧹 WebKit Speech recognition cleaned up");
  };

  // New function to trigger speech end after silence detection confirms it
  const triggerSpeechEnd = () => {
    if (hasDetectedSpeechRef.current) {
      if (debug) console.log("🛑 Speech end confirmed by webkit silence detection");
      onSpeechEnd();
    }
  };

  return {
    startSpeechRecognition,
    stopSpeechRecognition,
    cleanupSpeechRecognition,
    triggerSpeechEnd, // Export this for silence detection to use
    isRunning: isRunningRef.current,
    hasDetectedSpeech: hasDetectedSpeechRef.current
  };
};
